import React, { useEffect } from 'react';
import { Btn } from 'components/btn/Btn';
import { DealCard } from './DealCard';
import { getTopDeals } from 'common/api/deals/getTopDeals';
import { DealUIObject } from 'common/api/deals/types';
import { getBrandsFromDealIds } from 'common/api/constructor/getBrandsFromDealIds';
import { Brand } from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { DealsModal } from './DealsModal';
import { createStyles } from 'utils/createStyle';
import { useHistory } from 'react-router-dom';
import { getPath, Routes } from 'routes';
import { useConstructorDefaultParameters } from 'common/api/constructor/useConstructorDefaultParameters';

export interface DealUIObjectWithBrand extends DealUIObject {
    brand?: Brand;
}

const dealsSectionStyles = createStyles({
    container: ['mb-8 mt-4 mx-auto flex flex-col items-center justify-center max-w-[1024px]'],
    title: ['mb-4 text-dark-blue text-[24px] md:text-[29px] font-semibold'],
    dealsGrid: ['w-full flex flex-row flex-wrap items-start'],
    shopAllButton: ['mt-[28px] md:mt-[40px]'],
});

function uniq(id: string, index: number, array: string[]) {
    return array.indexOf(id) === index;
}

export interface DealsSectionProps {
    doNotFetchOwnDeals?: boolean;
    hideTitle?: boolean;
    hideMoreLink?: boolean;
    externalDeals?: DealUIObjectWithBrand[];
    location?: string;
}

export const DealsSection = ({
    externalDeals,
    doNotFetchOwnDeals,
    hideTitle,
    hideMoreLink,
    location,
}: DealsSectionProps) => {
    const defaultFilters = useConstructorDefaultParameters({});
    const history = useHistory();
    const [deals, setDeals] = React.useState<DealUIObjectWithBrand[]>([]);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [modalDeal, setModalDeal] = React.useState<DealUIObjectWithBrand | undefined>(undefined);
    const [dealCount, setDealCount] = React.useState(0);

    const goToAllDeals = () => {
        history.push(getPath(Routes.ShopTopDeals, {}));
    };

    useEffect(() => {
        if (doNotFetchOwnDeals) {
            if (externalDeals) {
                setDealCount(() => externalDeals.length);
                setDeals(() => externalDeals);
            }

            return;
        }

        if (!defaultFilters.current) {
            return;
        }

        getTopDeals()
            .then((_deals) => {
                setDealCount(() => _deals.length);
                const deals = _deals.slice(0, 6);
                setDeals(() => deals);

                const ids = deals.map((deal) => deal.affiliateId).filter(uniq);
                getBrandsFromDealIds(ids, defaultFilters.current).then((brands: any[]) => {
                    const indexedByBrandId = brands.reduce<Record<string, Brand>>((acc, brand) => {
                        acc[brand.affiliateBrandId] = brand;
                        return acc;
                    }, {});

                    const nextDeals = deals.map((deal) => ({
                        ...deal,
                        brand: indexedByBrandId[deal.affiliateId],
                    }));

                    setDeals(() => nextDeals);
                });
            })
            .catch(console.error);
    }, [JSON.stringify(defaultFilters.current), externalDeals, doNotFetchOwnDeals]);

    if (!deals || !deals.length) {
        return null;
    }

    return (
        <section className={dealsSectionStyles.container}>
            {!hideTitle && (
                <h3 className={dealsSectionStyles.title}>Top Online Deals &amp; Coupons</h3>
            )}

            <div className={dealsSectionStyles.dealsGrid}>
                {deals.map((deal, index) => (
                    <DealCard
                        key={index}
                        deal={deal}
                        brand={deal.brand}
                        onClick={() => {
                            setModalDeal(deal);
                            setModalOpen(true);
                        }}
                    />
                ))}
            </div>

            {dealCount && !hideMoreLink && (
                <Btn className={dealsSectionStyles.shopAllButton} onClick={goToAllDeals}>
                    Shop All
                    {/* Shop All {dealCount} */}
                </Btn>
            )}

            {modalDeal && (
                <DealsModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    dealWithBrand={modalDeal}
                    location={location}
                />
            )}
        </section>
    );
};
